import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageDescription from "./sections/PageDescription";
import PageContent from "./sections/PageContent";
import FooterSection from "./sections/FooterSection";
import InsightSection from "./sections/InsightSection";
import TracksDetail from "./sections/components/TracksDetail";
import SpotifyIcon from "../../../assets/images/admin/Spotify.svg";
import UIcon from "../../../assets/images/icons/u-icon.png";
import AppleIcon from "../../../assets/images/admin/Apple.svg";
import StartIcon from "../../../assets/images/admin/Star.svg";
import AmazonIcon from "../../../assets/images/admin/Amazon.svg";
import GroupIcon from "../../../assets/images/admin/Group-line.svg";
import InstaIcon from "../../../assets/images/admin/Instagram.png";
import TiktokIcon from "../../../assets/images/admin/Tiktok.png";
import YoutubeIcon from "../../../assets/images/admin/Youtube.png";
import StarOutlinedIcon from "../../../assets/images/admin/Star-outlined.svg";
import StarIcon from '../../../assets/images/icons/start-icon.svg';
import RadioPlayIcon from "../../../assets/images/admin/Radio-Play.png";
import {
  getArtistStatsBySources,
  getArtistChartStatsBySource,
  getPlatformStatsBySource,
  getPlatformChartsBySource,
  getMilestoneActivities,
  getRadioMilestones,
  getAudienceDetails,
} from "../../../redux/actions/dataAnalyst";
import { getCustomerPortalSession } from "../../../redux/actions";
import { PricingPlanModal } from "../../../components/PricingPlanModal";
import { showToast } from "../../../helper/ShowToast";
import {
  useMusicSourceData,
  useSocialPlatformData,
  getIconFromSource,
} from "./datahooks";
import {
  formatDateToMonthDay,
  getArtistNames,
  getCountryInfo,
} from "../../../helper/utility";
import DummyTrackCard from "../../../components/DummyTrackCard";
import DummyAudienecData from "../../../components/DummyAudienceData";
import AppTour from "../../../components/AppTour/AppTour";
import { dataAnalystTourSteps } from "../../../components/AppTour/ModuleTourSteps";
import { useTrackModuleVisit, MODULE_VISIT_TYPES } from "../../../hooks/useTrackModuleVisit";

const periodOptions = [
  { value: "all", label: "All time performance" },
  { value: "weekly", label: "Weekly performance" },
  { value: "monthly", label: "Monthly performance" },
  { value: "yearly", label: "Yearly performance" },
];

const periodOptionsAudience = [
  { value: "all", label: "All time audience" },
  { value: "weekly", label: "Weekly audience" },
  { value: "monthly", label: "Monthly audience" },
  { value: "yearly", label: "Yearly audience" },
];

const platforms = [
  { icon: UIcon, alt: "upncomer", name: "all" },
  { icon: SpotifyIcon, alt: "Spotify", name: "spotify" },
  { icon: AppleIcon, alt: "Apple Music", name: "apple_music" },
  { icon: AmazonIcon, alt: "Amazon Music", name: "amazon" },
  { icon: GroupIcon, alt: "Group", name: "soundcloud" },
];

const platformsAudience = [
  { icon: UIcon, alt: "upncomer", name: "all" },
  { icon: InstaIcon, alt: "Instagram", name: "instagram" },
  { icon: TiktokIcon, alt: "Tiktok", name: "tiktok" },
  { icon: YoutubeIcon, alt: "YouTube", name: "youtube" },
];

const sampleTracks = [
  {
    image: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=80&h=80&fit=crop',
    name: 'Summer Vibes',
    artist: 'The Wave Makers',
    date: '2024-03-15',
    label: 'Ocean Records',
    channel: 'Spotify',
    icon: null
  },
  {
    image: 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=80&h=80&fit=crop',
    name: 'Midnight Dreams',
    artist: 'Luna Eclipse',
    date: '2024-02-20',
    label: 'Stellar Music',
    channel: 'Apple Music',
    icon: null
  },
  {
    image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=80&h=80&fit=crop',
    name: 'Electric Pulse',
    artist: 'Neon Knights',
    date: '2024-01-10',
    label: 'Synth Wave Records',
    channel: 'YouTube',
    icon: null
  }
];

const onViewContentCreator = () => {
  alert("Content Creator is a Work in Progress.");
};

export const DataAnalyst = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hash } = useLocation()
  const [runTour, setRunTour] = useState(false)
  // useTrackModuleVisit(MODULE_VISIT_TYPES.DATA_ANALYST, "main");
  const [selectedPeriod, setSelectedPeriod] = useState("all");
  const [selectedPeriodAudience, setSelectedPeriodAudience] = useState("all");
  const [selectedMusicPlatform, setSelectedMusicPlatform] = useState("all");
  const [selectedSocialPlatform, setSelectedSocialPlatform] = useState("all");

  const [loadMusicChart, setLoadMusicChart] = useState(false);
  const [loadAudienceChart, setLoadAudienceChart] = useState(false);
  const [loadMilestones, setLoadMilestones] = useState(false);
  const [loadPlaylists, setLoadPlaylists] = useState(false);
  const [loadAudienceData, setLoadAudienceData] = useState(false);
  const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);

  const { hasActiveSubscription } = useSelector(
    (state) => state.billing,
    shallowEqual
  );

  const {
    artistStatsBySource,
    artistChartStatsBySource,
    platformStatsBySource,
    platformChartStatsBySource,
    milestoneActivities,
    radioMilestonesData,
    audienceDetails,
  } = useSelector(
    (state) => state.dataanalyst,
    shallowEqual
  );

  const musicMetrics = useMusicSourceData(
    artistStatsBySource,
    artistChartStatsBySource?.all_data,
    selectedPeriod,
    selectedMusicPlatform
  )

  const socialMetrics = useSocialPlatformData(
    platformStatsBySource,
    platformChartStatsBySource,
    selectedPeriodAudience,
    selectedSocialPlatform
  );

  const milestoneList = milestoneActivities?.data
    ? milestoneActivities.data
    : Array.isArray(milestoneActivities)
      ? milestoneActivities
      : [];

  const filteredMilestones = useMemo(
    () => (milestoneList || []).filter((item) => item.source !== "radio") || [],
    [milestoneList]
  );
  const firstThreeMilestones =
    filteredMilestones?.length > 0
      ? (filteredMilestones || [])?.slice(0, 3)
      : [];
  const radioList = radioMilestonesData?.data
    ? radioMilestonesData.data
    : Array.isArray(radioMilestonesData)
      ? radioMilestonesData
      : [];

  const firstThreeRadioMilestones = radioList?.length > 0 ? radioList.slice(0, 3) : [];

  useEffect(() => {
    if (hash === "#start-tour") {
      setRunTour(true)
    }
  }, [hash])

  // Music performance
  useEffect(() => {
    if (!hasActiveSubscription) return;

    const params = { type: selectedPeriod, source: selectedMusicPlatform };
    dispatch(getArtistStatsBySources(toast, params, setLoadMusicChart));
    dispatch(getArtistChartStatsBySource(toast, params));
  }, [dispatch, hasActiveSubscription, selectedPeriod, selectedMusicPlatform]);

  // Social audience
  useEffect(() => {
    if (!hasActiveSubscription) return;

    const params = { type: selectedPeriodAudience, source: selectedSocialPlatform };
    dispatch(getPlatformStatsBySource(toast, params, setLoadAudienceChart));
    dispatch(getPlatformChartsBySource(toast, params));
  }, [
    dispatch,
    hasActiveSubscription,
    selectedPeriodAudience,
    selectedSocialPlatform,
  ]);

  // Milestones
  useEffect(() => {
    if (!hasActiveSubscription) return;

    dispatch(getMilestoneActivities(toast, { source: "all" }, "MILESTONE", setLoadMilestones));
    dispatch(getRadioMilestones(toast, {}, setLoadPlaylists));
    dispatch(getAudienceDetails(toast, { source: "spotify" }, setLoadAudienceData));
  }, [dispatch, hasActiveSubscription]);

  // Handle payment
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("status");
    if (!status) return;

    setTimeout(() => {
      if (status === "success")
        showToast("success", "Success! Your subscription has been activated.");
      if (status === "cancel")
        showToast("error", "Payment cancelled");
    }, 100);

    params.delete("status");
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const handleProClick = useCallback(async () => {
    if (hasActiveSubscription) {
      await dispatch(getCustomerPortalSession());
    } else {
      setShowPricingPlanModal(true);
    }
  }, [dispatch, hasActiveSubscription]);

  const navigateToCatalog = useCallback(
    () => navigate("/admin/data-analyst/catalog"),
    [navigate]
  );

  const navigateToMilestones = useCallback(
    () => navigate("/admin/data-analyst/milestones"),
    [navigate]
  );

  const navigateToRadioPlay = useCallback(
    () => navigate("/admin/data-analyst/radioplay"),
    [navigate]
  );

  return (
    <div className="DataAnalyst-page py-5">
      <AppTour
        tourSteps={dataAnalystTourSteps}
        runTour={runTour}
        setRunTour={setRunTour}
      />
      <div className="container max-w-full">
        <PageDescription
          title="Data Analyst"
          description="Unlock insights into your music performance and audience data across platforms."
        />

        <div data-tour="music-performance">
          <PageContent
            buttonLabel="View Catalog"
            periodOptions={periodOptions}
            selectedPeriod={selectedPeriod}
            platforms={platforms}
            selectedPlatform={selectedMusicPlatform}
            onPeriodChange={(e) => setSelectedPeriod(e.target.value)}
            onPlatformsChange={setSelectedMusicPlatform}
            isLoading={loadMusicChart}
            onButtonClick={navigateToCatalog}
            onProClick={handleProClick}
            metrics={musicMetrics}
            hasActiveSubscription={hasActiveSubscription}
            isMarginBottom
          />
        </div>

        <div data-tour="social-performance">
          {socialMetrics?.length > 0 && (
            <PageContent
              buttonLabel="View Content Creator"
              greyedOutButton
              periodOptions={periodOptionsAudience}
              selectedPeriod={selectedPeriodAudience}
              platforms={platformsAudience}
              selectedPlatform={selectedSocialPlatform}
              onPeriodChange={(e) => setSelectedPeriodAudience(e.target.value)}
              onPlatformsChange={setSelectedSocialPlatform}
              isLoading={loadAudienceChart}
              hasActiveSubscription={hasActiveSubscription}
              metrics={socialMetrics}
              isMarginBottom
            />
          )}
        </div>

        {/* Grid Listing Section */}
        <div className="performance-section">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <div className="grid-item xl:row-span-2" data-tour="milestones">
              <FooterSection
                titleIcon={StartIcon}
                title="Milestones"
                buttonLabel={"View Milestones"}
                showButton={hasActiveSubscription}
                onButtonClick={navigateToMilestones}
              >
                <div className="w-full flex flex-col gap-2">
                  {hasActiveSubscription ?
                    <>
                      {firstThreeMilestones.map((item, index) => (
                        <TracksDetail
                          key={index}
                          trackData={{
                            image: item.track_info.avatar,
                            name: item.track_info.title,
                            artist: getArtistNames(item.track_info.artists),
                            link: item.track_info.site_url,
                            date: formatDateToMonthDay(item.activity_date),
                            label: item.activity_text,
                            icon: getIconFromSource(item.source),
                          }}
                        />
                      ))}
                    </> :
                    <div className="flex flex-col items-center">
                      {sampleTracks.map((track, index) => (
                        <DummyTrackCard
                          key={index}
                          trackData={track}
                          isPro={false}
                          onProClick={handleProClick}
                        />
                      ))}
                    </div>
                  }
                </div>
              </FooterSection>
            </div>
            <div className="grid-item xl:row-span-2" data-tour="radio-play">
              <FooterSection
                titleIcon={RadioPlayIcon}
                title="Radio Play"
                buttonLabel={"View Radio Data"}
                showButton={hasActiveSubscription}
                onButtonClick={navigateToRadioPlay}
              >
                {hasActiveSubscription ?
                  <>
                    {firstThreeRadioMilestones.map((item, index) => (
                      <TracksDetail
                        key={index}
                        trackData={{
                          image: item.track_info.avatar,
                          name: item.track_info.title,
                          artist: getArtistNames(item.track_info.artists),
                          link: item.track_info.site_url,
                          date: formatDateToMonthDay(item.activity_date),
                          label: item.activity_text,
                        }}
                      />
                    ))}
                  </> :
                  <div className="flex flex-col items-center">
                    {sampleTracks.map((track, index) => (
                      <DummyTrackCard
                        key={index}
                        trackData={track}
                        isPro={false}
                        onProClick={handleProClick}
                      />
                    ))}
                  </div>
                }

              </FooterSection>
            </div>
            <div className="grid grid-item gap-4 xl:row-span-2">
              <div className="grid-item" data-tour="audience-data">
                <FooterSection
                  titleIcon={StarOutlinedIcon}
                  title="Audience Demographics"
                  buttonLabel={"View Demographics"}
                  onButtonClick={navigateToCatalog}
                >
                  {hasActiveSubscription ?
                    <div className="element-wrapper text-[12.5px] font-manrope">
                      <div className="flex flex-wrap justify-between gap-2 mb-[11px]">
                        <div className="title font-bold">
                          Top City<span className="ms-2">🏙️</span>
                        </div>
                        <div className="value font-normal">
                          {typeof audienceDetails?.topCity === "object"
                            ? audienceDetails?.topCity?.name
                            : audienceDetails?.topCity}
                        </div>
                      </div>
                      <div className="flex flex-wrap justify-between gap-2 mb-[11px]">
                        <div className="title font-bold">
                          Top Country<span className="ms-2"></span>
                        </div>
                        <div className="value font-normal">
                          {typeof audienceDetails?.topCountry === "object"
                            ? getCountryInfo(audienceDetails?.topCountry?.code)
                              .name
                            : audienceDetails?.topCountry}
                        </div>
                      </div>
                      <div className="flex justify-between gap-2 mb-[15px]">
                        <div className="title font-bold flex items-center">
                          <span className="text-nowrap">Growth Market</span>
                          <span className={`ml-2 ring-4 ring-orange-500 squared-lg rounded fi fi-${audienceDetails?.growthMarket?.market?.toLowerCase()}`}></span>
                        </div>
                        <div className="value font-normal text-right">
                          {typeof audienceDetails?.growthMarket === "object"
                            ?
                            audienceDetails?.growthMarket?.message
                            : ""}
                        </div>
                      </div>
                    </div> :
                    <div className="flex flex-col items-center">
                      <DummyAudienecData onProClick={handleProClick} />
                    </div>
                  }
                </FooterSection>
              </div>
              <div className="grid-item" data-tour="popularity-score">
                <FooterSection
                  title="Popularity Score"
                  buttonLabel={"View Score by Song"}
                  onButtonClick={navigateToCatalog}
                >
                  {hasActiveSubscription ?
                    <div className="info text-[50px] font-semibold leading-tight text-center mb-[17px]">
                      {artistStatsBySource?.popularity_score_all}%
                    </div> :
                    <div className="relative w-full">
                      <div className="flex flex-col items-center">
                        <div className="info text-[50px] font-semibold opacity-50 leading-tight text-center mb-[17px]">
                          5%
                        </div>
                        <>
                          <div className="absolute inset-0 bg-gray-200/60 backdrop-blur-[1px] z-10 rounded" />

                          <div className="absolute inset-0 z-20 flex items-center justify-center">
                            <button
                              type="button"
                              className={`btn-custom3 flex items-center gap-2`}
                              onClick={handleProClick}
                            >
                              Go Pro
                              <img
                                src={StarIcon}
                                alt="Star"
                                className="h-[20px] w-[20px] min-w-[20px]"
                              />
                            </button>
                          </div>
                        </>
                      </div>
                    </div>
                  }
                </FooterSection>
              </div>
            </div>
            <div className="grid-item xl:row-span-2" data-tour="amplitude-insights">
              <InsightSection
                hasActiveSubscription={hasActiveSubscription}
                topTrack={artistStatsBySource?.topTrack}
                onProClick={handleProClick}
              />
            </div>
          </div>

        </div>
      </div>

      <PricingPlanModal
        isOpen={showPricingPlanModal}
        onClose={() => setShowPricingPlanModal(false)}
        returnUrl={window.location.origin + "/admin/data-analyst"}
      />
    </div>
  );
};
